/**
 * PromptDialog - Replaces window.prompt() with a proper modal
 *
 * Props:
 *   isOpen       - Whether the dialog is visible
 *   onSubmit     - Called with the input value when user submits
 *   onCancel     - Called when user cancels or clicks backdrop
 *   title        - Dialog title
 *   message      - Dialog message (string or JSX)
 *   defaultValue - Initial value for the input
 *   placeholder  - Input placeholder
 *   submitText   - Text for submit button (default: "Submit")
 *   cancelText   - Text for cancel button (default: "Cancel")
 */
import React, { useState, useEffect, useRef } from 'react';

interface PromptDialogProps {
  isOpen: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  title?: string;
  message?: React.ReactNode;
  defaultValue?: string;
  placeholder?: string;
  submitText?: string;
  cancelText?: string;
}

const PromptDialog = ({
  isOpen,
  onSubmit,
  onCancel,
  title = 'Input',
  message = '',
  defaultValue = '',
  placeholder = '',
  submitText = 'Submit',
  cancelText = 'Cancel',
}: PromptDialogProps) => {
  const [value, setValue] = useState<string>(defaultValue);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
      // Focus the input after render
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, defaultValue]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(value);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onCancel} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCancel(); }} role="presentation"
        />
        {/* Dialog */}
        <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>

          {message && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {message}
            </p>
          )}

          <form onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
              placeholder={placeholder}
              className="mt-4 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                {cancelText}
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {submitText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PromptDialog;
